import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import {
  Alert,
  AlertIcon,
  AlertDescription,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text as ChakraText,
  VStack,
  chakra,
  Link,
  Select,
  Box,
} from '@chakra-ui/react'
import type { FieldProps, FormikErrors } from 'formik'
import { Field, Formik } from 'formik'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import useToast from '../../../hooks/useToast'
import { MediaLibrarySearchFolderResponse } from '../../../interfaces'
import { MLContext } from '../../../providers'
import { CustomMetadataContext } from '../../../providers/CustomMetadataProvider'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import CMUploadForm from '../Details/CustomMetadataForm/CMUploadForm'
import { getCmFieldValidatorMap } from './uploadUtils'
import {useGetQueryParams} from '../../../hooks/useGetQueryParams'
import { Base64 } from 'js-base64'
import { useHistory } from 'react-router-dom'

const CM_FIELD_PREFIX = `###CM_FIELD_PREFIX${Math.random()}###`.replace('.', '')

interface FormProps {
  folderName: string
  customMetadataSelect: string
}

interface Props {
  isOpen: boolean
  isSecondaryLocation: boolean
  onClose(): void
}

interface FolderErrorResponse {
  message?: string
  help?: string
  errors?: Array<{ field?: string; error?: string }>
}

const CreateFolder = ({
  isOpen,
  isSecondaryLocation,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const toast = useToast()
  const [
    { folderPath, drawerFolderPath, refetchDrawerFolders },
    dispatch
  ] = useContext(MLContext)
  const [showCmForm, setShowCmForm] = useState(false)
  const { cmFields } = useContext(CustomMetadataContext)
  const queryFromUrl = useGetQueryParams()
  const history = useHistory()

  useEffect(() => {
    if (isOpen) {
      setShowCmForm(false)
    }
  }, [isOpen])
  const mappedCmFields = useMemo(
    () =>
      cmFields.length
        ? cmFields.map(f => ({
          ...f,
          _name: f.name,
          name: `${CM_FIELD_PREFIX}${f.name}`,
        }))
        : [],
    [cmFields],
  )

  const initialCmValues = useMemo(
    () =>
      Object.fromEntries(
        mappedCmFields.map(f => [f.name, f.schema.defaultValue]),
      ),
    [mappedCmFields],
  )
  const validator = useMemo(() => {
    const mandatoryFieldSet = new Set(
      cmFields.filter(f => f.schema.isValueRequired).map(f => f.name),
    )
    const validatorMap = getCmFieldValidatorMap(cmFields)
    return async (fields: FormProps): Promise<FormikErrors<FormProps>> => {
      // validate custom-metadata fields
      const result = await Promise.all(
        Object.entries(fields)
          .filter(([name]) => name.startsWith(CM_FIELD_PREFIX))
          .map(async ([name, value]) => {
            const cmFieldName = name.replace(CM_FIELD_PREFIX, '')
            if (value === null) {
              if (mandatoryFieldSet.has(cmFieldName)) {
                return {
                  isValid: false,
                  fieldName: name,
                  error: 'Mandatory field',
                }
              }
              return { isValid: true, fieldName: name, value }
            }
            const yupSchema = validatorMap.get(cmFieldName)
            if (!yupSchema) {
              throw Error('Validator not found')
            }
            try {
              await yupSchema.validate(value)
              return { isValid: true, fieldName: name, value }
            } catch (err: any) {
              return {
                isValid: false,
                fieldName: name,
                error: err?.errors[0],
              }
            }
          }),
      )
      const errorResult = Object.fromEntries(
        result
          .filter(r => !r.isValid)
          .map(({ fieldName, error }) => [`${fieldName}`, error]),
      )
      // validate folderName
      if (['', '.', '..'].includes(fields.folderName)) {
        errorResult.folderName = t(
          'dot_and_dot_dot_can_not_be_used_as_folder_name',
          "'.' & '..' can not be used as folder-name",
        )
      } else if (fields.folderName.includes('/')) {
        errorResult.folderName = t(
          'backslash_can_not_be_used_in_folder_name',
          "'/' can not be used in folder-name",
        )
      }
      return errorResult
    }
  }, [cmFields])

  const location = isSecondaryLocation ? drawerFolderPath : folderPath

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxW="2xl">
        <Formik<FormProps>
          initialValues={{
            folderName: 'New Folder',
            customMetadataSelect: 'cm_default',
            ...initialCmValues,
          }}
          onSubmit={async (values, helpers): Promise<void> => {
            const reqBody: {
              folderName: string
              parentFolderPath: string
              customMetadata?: { [key: string]: string }
            } = {
              folderName: values.folderName,
              parentFolderPath: isSecondaryLocation
                ? drawerFolderPath
                : folderPath,
            }

            const cmEntries = Object.entries({ ...values })
              .filter(([name]) => name.startsWith(CM_FIELD_PREFIX))
              .map(([name, val]) => [name.replace(CM_FIELD_PREFIX, ''), val])
            if (cmEntries.length) {
              reqBody.customMetadata = Object.fromEntries(cmEntries)
            }

            if(values.customMetadataSelect === "cm_default")
              {
                reqBody.customMetadata = undefined;
              }
            const response = (await request.post(
              '/api/v2/folder',
              reqBody,
            )) as MediaLibrarySearchFolderResponse | FolderErrorResponse

            if (!request.error) {
              if ((response as MediaLibrarySearchFolderResponse).alreadyExist) {
                void refetchDrawerFolders();
                toast({
                  description: <Trans t={t} i18nKey="folder_already_exists_toast">
                    Folder already exists. Click
                    <Link onClick={(event) => {
                      event.preventDefault()
                      history.push(
                        `/media-library/${Base64.encode((response as MediaLibrarySearchFolderResponse).folderPath, true)}${
                          queryFromUrl as string
                        }`
                      )
                    }}>
                      {" here "}
                    </Link>
                    to view.
                  </Trans>,
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                onClose()
                return;
              }
              dispatch({
                type: MediaLibraryActions.ADD_TO_RECENT_FILE_FOLDER_LIST,
                payload: [response as MediaLibrarySearchFolderResponse],
              })
              void refetchDrawerFolders()
              toast({
                description: 'Folder created successfully.',
                status: 'success',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })
              onClose()
            } else {
              helpers.setErrors(
                Object.fromEntries(
                  ((response as FolderErrorResponse).errors ?? [])
                    .filter(e => e.field?.length && e.error?.length)
                    .map(e => [`${CM_FIELD_PREFIX}${e.field}`, e.error]),
                ),
              )
              toast({
                description:
                  (response as FolderErrorResponse)?.message ??
                  'Error in creating folder, Please try again after some time.',
                status: 'error',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })
            }
          }}
          validationSchema={Yup.object().shape({
            folderName: Yup.string().required('Required'),
          })}
          validate={validator}
        >
          {({ handleChange, values, submitForm, dirty, isValid, isSubmitting }) => (
            <>
              <ModalHeader>{t('create_folder', 'Create Folder')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody maxH="lg" overflowY="scroll">
                <VStack spacing={4} alignItems="flex-start">
                  <Trans t={t} i18nKey="create_folder_description">
                    <ChakraText color="appBattleshipGrey" maxWidth="100%">
                      This folder will be created under{' '}
                      <chakra.span color="appDarkIndigo" fontWeight="medium">
                        Home{{ location }}
                      </chakra.span>
                      . All characters except alphabets and numbers will be
                      converted into _
                    </ChakraText>
                  </Trans>
                  <Field name="folderName">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.folderName && form.touched.folderName,
                        )}
                        isRequired
                      >
                        <FormLabel>{t('name', 'Name')}</FormLabel>
                        <Input {...field} placeholder="New Folder" />
                        <FormErrorMessage marginBottom="-2.0408em">
                          {form.errors.folderName}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <br />
                  <Field name="customMetadataSelect" >
                    {({
                      form,
                    }: FieldProps<string, { customMetadataSelect: string }>) => (
                      <FormControl
                        style={{ maxWidth: "400px" }}
                        marginTop="1em"
                        isInvalid={Boolean(
                          form.errors.customMetadataSelect &&
                          form.touched.customMetadataSelect,
                        )}
                      >
                        <FormLabel
                          htmlFor="customMetadataSelect"
                          fontWeight={700}
                          mb={"16px"}
                        >
                          {t('custom_metadata', 'Custom Metadata')}
                        </FormLabel>
                        <Alert status="info" >
                          <AlertIcon
                            height="1em"
                            width="1em"
                            alignItems="flex-start"
                            marginTop="4px"

                          />
                          <AlertDescription fontSize="sm">
                            {t('custom_metadata_default_alert', 'Default values associated with custom metadata fields\
                             will persist even after the fields are cleared via the form.')}
                          </AlertDescription>
                        </Alert>
                        <Select
                          marginTop={"16px"}
                          name="customMetadataSelect"
                          value={values?.customMetadataSelect}
                          onChange={event => {
                            handleChange(event)
                            setShowCmForm(Boolean(event.target.value === "cm_form"))
                          }}
                        >
                          <option value='cm_default'> {t('set_default_values', 'Set default values')}</option>
                          <option value='cm_form'>{t('show_metadata_form', 'set custom metadata')}</option>
                        </Select>

                        <FormErrorMessage>
                          {form.errors.customMetadataSelect}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {showCmForm ? (
                    mappedCmFields.length ? (
                      <CMUploadForm
                        cmFields={mappedCmFields}
                        renderFromHeader={false}
                      />
                    ) : (
                      <Alert status="info" minW="auto" spacing="1em">
                        <AlertIcon height="1em" width="1em" />
                        <AlertDescription fontSize="sm">
                          <Trans
                            t={t}
                            i18nKey="no_cmf_to_show_add_some_in_ml_settings"
                          >
                            No custom metadata fields to show. Add some in
                            <Link
                              href="/dashboard/settings/media-library"
                              fontSize="sm"
                            >
                              media library settings
                            </Link>
                            .
                          </Trans>
                        </AlertDescription>
                      </Alert>
                    )
                  ) : null}
                </VStack>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button onClick={onClose} variant="outline">
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting || !isValid}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('create', 'Create')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default CreateFolder
